/**
 * Image Zoom Component
 * Lightweight zoom functionality for product images
 * Supports click-to-zoom, pinch-to-zoom, and pan when zoomed
 */

class ImageZoom {
  constructor(container) {
    this.container = container;
    this.image = container.querySelector('img');
    this.isZoomed = false;
    this.scale = 1;
    this.maxScale = 3;
    this.translateX = 0;
    this.translateY = 0;
    
    // Touch/gesture tracking
    this.initialDistance = 0;
    this.initialScale = 1;
    this.startX = 0;
    this.startY = 0;
    this.isDragging = false;
    this.hasMoved = false;
    this.clickStartTime = 0;
    
    // Create reset button
    this.createResetButton();
    
    this.init();
  }
  
  createResetButton() {
    this.resetButton = document.createElement('button');
    this.resetButton.className = 'image-zoom-reset';
    this.resetButton.setAttribute('aria-label', 'Exit zoom');
    this.resetButton.setAttribute('type', 'button');
    this.resetButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
    this.resetButton.style.display = 'none';
    this.container.appendChild(this.resetButton);
    
    // Handle both click and touch events
    const handleReset = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.zoomOut();
    };
    
    this.resetButton.addEventListener('click', handleReset, true);
    this.resetButton.addEventListener('touchend', handleReset, true);
    
    // Prevent touch events from bubbling to zoom container
    this.resetButton.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, true);
    
    this.resetButton.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, true);
  }
  
  init() {
    if (!this.image) {
      return;
    }
    
    // Prevent link clicks when zooming
    const links = this.container.querySelectorAll('a');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        // Always prevent default to allow zoom click
        e.preventDefault();
        e.stopPropagation();
      });
    });
    
    // Desktop: Click to toggle zoom (only if not dragging)
    this.container.addEventListener('click', (e) => {
      // Don't trigger on the reset button
      if (e.target.closest('.image-zoom-reset')) {
        return;
      }
      
      // Don't toggle if user was dragging
      if (this.hasMoved) {
        return;
      }
      
      e.preventDefault();
      e.stopPropagation();
      this.toggleZoom(e);
    }, true); // Use capture phase
    
    // Mobile: Touch events for pinch-to-zoom and pan
    // Use capture phase to intercept before swiper
    this.container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false, capture: true });
    this.container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false, capture: true });
    this.container.addEventListener('touchend', this.handleTouchEnd.bind(this), { capture: true });
    
    // Desktop: Mouse events for pan when zoomed
    this.container.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.container.addEventListener('mousemove', this.handleMouseMove.bind(this));
    this.container.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.container.addEventListener('mouseleave', () => {
      this.isDragging = false;
      if (this.isZoomed) {
        this.container.style.cursor = 'grab';
      }
    });
    
    // Prevent context menu on long press
    this.container.addEventListener('contextmenu', (e) => {
      if (this.isZoomed) e.preventDefault();
    });
  }
  
  toggleZoom(e) {
    if (this.isZoomed) {
      this.zoomOut();
    } else {
      this.zoomIn(e);
    }
  }
  
  zoomIn(e) {
    this.isZoomed = true;
    this.scale = this.maxScale;
    
    // Zoom into center for better UX
    this.translateX = 0;
    this.translateY = 0;
    
    this.updateTransform();
    this.container.classList.add('zoomed');
    this.container.style.cursor = 'grab';
    
    // Show reset button
    this.resetButton.style.display = 'flex';
    
    // Disable swiper when zoomed to allow panning
    this.disableSwiper();
  }
  
  zoomOut() {
    this.isZoomed = false;
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
    this.updateTransform();
    this.container.classList.remove('zoomed');
    this.container.style.cursor = 'zoom-in';
    
    // Hide reset button
    this.resetButton.style.display = 'none';
    
    // Re-enable swiper when zoomed out
    this.enableSwiper();
  }
  
  disableSwiper() {
    // Find the parent swiper container
    const swiperContainer = this.container.closest('.swiper');
    if (swiperContainer && swiperContainer.swiper) {
      this.swiperInstance = swiperContainer.swiper;
      // Disable touch events on swiper
      this.swiperInstance.allowTouchMove = false;
      this.swiperInstance.allowSlideNext = false;
      this.swiperInstance.allowSlidePrev = false;
    }
  }
  
  enableSwiper() {
    // Re-enable swiper if we had disabled it
    if (this.swiperInstance) {
      this.swiperInstance.allowTouchMove = true;
      this.swiperInstance.allowSlideNext = true;
      this.swiperInstance.allowSlidePrev = true;
      this.swiperInstance = null;
    }
  }
  
  updateTransform() {
    this.image.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }
  
  // Touch event handlers
  handleTouchStart(e) {
    // Don't handle if touch started on the reset button
    if (e.target.closest('.image-zoom-reset')) {
      return;
    }
    
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      e.stopPropagation();
      this.initialDistance = this.getDistance(e.touches[0], e.touches[1]);
      this.initialScale = this.scale;
      this.isDragging = false; // Cancel any ongoing drag
    } else if (e.touches.length === 1 && this.isZoomed) {
      // Pan - prevent swiper from handling this
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = true;
      this.lastX = e.touches[0].clientX;
      this.lastY = e.touches[0].clientY;
    }
  }
  
  handleTouchMove(e) {
    // Don't handle if touch is on the reset button
    if (e.target.closest('.image-zoom-reset')) {
      return;
    }
    
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      e.stopPropagation();
      const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
      const scaleChange = currentDistance / this.initialDistance;
      this.scale = Math.min(this.maxScale, Math.max(1, this.initialScale * scaleChange));
      
      if (this.scale > 1) {
        if (!this.isZoomed) {
          this.isZoomed = true;
          this.container.classList.add('zoomed');
          this.resetButton.style.display = 'flex';
          this.disableSwiper();
        }
      }
      
      this.updateTransform();
    } else if (e.touches.length === 1 && this.isDragging && this.isZoomed) {
      // Pan
      e.preventDefault();
      e.stopPropagation();
      
      // Calculate movement delta
      const deltaX = e.touches[0].clientX - this.lastX;
      const deltaY = e.touches[0].clientY - this.lastY;
      
      // Update last position
      this.lastX = e.touches[0].clientX;
      this.lastY = e.touches[0].clientY;
      
      // Apply movement
      this.translateX = this.constrainTranslate(this.translateX + deltaX, 'x');
      this.translateY = this.constrainTranslate(this.translateY + deltaY, 'y');
      
      this.updateTransform();
    }
  }
  
  handleTouchEnd(e) {
    if (e.touches.length === 0) {
      this.isDragging = false;
      
      // If zoomed out completely, reset
      if (this.scale <= 1.1) {
        this.zoomOut();
      }
    }
    
    // Also check if we're back to 2 fingers (end of one finger but still pinching)
    if (e.touches.length === 2) {
      this.isDragging = false;
    }
  }
  
  // Mouse event handlers (desktop)
  handleMouseDown(e) {
    if (!this.isZoomed) return;
    
    e.preventDefault();
    this.isDragging = true;
    this.hasMoved = false;
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    this.container.style.cursor = 'grabbing';
  }
  
  handleMouseMove(e) {
    if (!this.isDragging || !this.isZoomed) return;
    
    e.preventDefault();
    
    // Calculate movement delta
    const deltaX = e.clientX - this.lastX;
    const deltaY = e.clientY - this.lastY;
    
    // Mark as moved if there's significant movement
    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      this.hasMoved = true;
    }
    
    // Update last position
    this.lastX = e.clientX;
    this.lastY = e.clientY;
    
    // Apply movement
    this.translateX = this.constrainTranslate(this.translateX + deltaX, 'x');
    this.translateY = this.constrainTranslate(this.translateY + deltaY, 'y');
    
    this.updateTransform();
  }
  
  handleMouseUp() {
    this.isDragging = false;
    
    // Reset hasMoved after a short delay to prevent click from firing
    setTimeout(() => {
      this.hasMoved = false;
    }, 50);
    
    if (this.isZoomed) {
      this.container.style.cursor = 'grab';
    }
  }
  
  // Helper methods
  getDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  constrainTranslate(value, axis) {
    const rect = this.container.getBoundingClientRect();
    const imageRect = this.image.getBoundingClientRect();
    
    if (axis === 'x') {
      const scaledWidth = rect.width * this.scale;
      const maxTranslate = (scaledWidth - rect.width) / 2;
      return Math.min(maxTranslate, Math.max(-maxTranslate, value));
    } else {
      const scaledHeight = rect.height * this.scale;
      const maxTranslate = (scaledHeight - rect.height) / 2;
      return Math.min(maxTranslate, Math.max(-maxTranslate, value));
    }
  }
  
  destroy() {
    this.zoomOut();
    // Remove reset button
    if (this.resetButton && this.resetButton.parentNode) {
      this.resetButton.parentNode.removeChild(this.resetButton);
    }
    // Event listeners will be garbage collected with the element
  }
}

// Initialize zoom on all product images
function initializeImageZoom() {
  const zoomContainers = document.querySelectorAll('[data-image-zoom]');
  
  zoomContainers.forEach((container) => {
    // Skip if already initialized
    if (container._imageZoom) {
      return;
    }
    
    const zoom = new ImageZoom(container);
    container._imageZoom = zoom;
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeImageZoom);
} else {
  // DOM already loaded
  initializeImageZoom();
}

// Re-initialize after page transitions
document.addEventListener('pageTransitionComplete', initializeImageZoom);

